import React, { useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import BadgeIcon from "@mui/icons-material/Badge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useForm, Controller } from "react-hook-form";
import { useApiMutation } from "src/hooks/useApiMutation";
import PhoneInputField from "src/components/Common/PhoneInputField";
import type { ICreateVisitorPayload } from "../type/newVisitorType";

interface Props {
	open: boolean;
	handleClose: () => void;
	onSuccessCallback?: (createdData?: any) => void;
}

interface FormValues {
	visitor_name: string;
	email: string;
	password: string;
	phone: string;
	organization: string;
	designation: string;
	address: string;
	ticket_quantity: number;
	payment_status: string;
	payment_method: string;
	ticket_note: string;
}

const CreateVisitor: React.FC<Props> = ({ open, handleClose, onSuccessCallback }) => {
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			visitor_name: "",
			email: "",
			password: "12345678",
			phone: "+880",
			organization: "",
			designation: "",
			address: "",
			ticket_quantity: 1,
			payment_status: "paid",
			payment_method: "cash",
			ticket_note: "",
		},
	});

	const { mutate: createVisitor, isPending } = useApiMutation<
		ICreateVisitorPayload,
		any
	>({
		method: "POST",
		endPoint: "/api/v1/admin/visitor",
		invalidateQueries: ["Visitors"],
	});

	const onSubmit = (data: FormValues) => {
		const payload: ICreateVisitorPayload = {
			name: data.visitor_name.trim(),
			email: data.email.trim(),
			password: data.password ? data.password.trim() : "12345678",
			phone: data.phone?.trim() || undefined,
			organization: data.organization.trim() || undefined,
			designation: data.designation.trim() || undefined,
			address: data.address.trim() || undefined,
			ticket: {
				quantity: Number(data.ticket_quantity) || 1,
				payment_status: data.payment_status,
				payment_method: data.payment_method,
				payment_details: {
					note: data.ticket_note.trim() || undefined,
				},
			},
		};

		createVisitor(payload, {
			onSuccess: (res: any) => {
				reset();
				handleClose();
				onSuccessCallback?.(res);
			},
		});
	};

	const onDialogClose = () => {
		if (isPending) return;
		reset();
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={onDialogClose}
			maxWidth="md"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<PersonAddAlt1Icon color="primary" />
					<Typography variant="h4" fontWeight={600}>
						Create New Visitor
					</Typography>
				</Box>
				<IconButton onClick={onDialogClose} size="small" disabled={isPending}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ pt: 2 }}>
				{/* Section 1: Personal Details */}
				<Box sx={{ mb: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
						<BadgeIcon color="primary" fontSize="small" />
						<Typography variant="subtitle1" fontWeight={600} color="primary.main">
							Personal & Contact Information
						</Typography>
					</Box>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								label="Full Name"
								required
								{...register("visitor_name", {
									required: "Full name is required",
								})}
								error={Boolean(errors.visitor_name)}
								helperText={errors.visitor_name?.message}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								type="email"
								label="Email Address"
								required
								{...register("email", {
									required: "Email is required",
									pattern: {
										value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
										message: "Invalid email address",
									},
								})}
								error={Boolean(errors.email)}
								helperText={errors.email?.message}
							/>
						</Grid>



						<Grid item xs={12} sm={6}>
							<PhoneInputField
								name="phone"
								control={control}
								label="Phone Number"
								required
								defaultCountry="bd"
								placeholder="Enter mobile number"
								error={Boolean(errors.phone)}
								helperText={errors.phone?.message}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								type={showPassword ? "text" : "password"}
								label="Password"
								required
								{...register("password", {
									required: "Password is required",
								})}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword((prev) => !prev)}
												edge="end"
												size="small"
											>
												{showPassword ? (
													<VisibilityOffIcon fontSize="small" />
												) : (
													<VisibilityIcon fontSize="small" />
												)}
											</IconButton>
										</InputAdornment>
									),
								}}
								error={Boolean(errors.password)}
								helperText={errors.password?.message || "Default: 12345678"}
							/>
						</Grid>
					</Grid>
				</Box>

				<Divider sx={{ my: 2 }} />

				{/* Section 2: Ticket & Payment Details */}
				<Box sx={{ mb: 1 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
						<ConfirmationNumberIcon color="primary" fontSize="small" />
						<Typography variant="subtitle1" fontWeight={600} color="primary.main">
							Ticket & Payment Details
						</Typography>
					</Box>

					<Paper
						variant="outlined"
						sx={{
							p: 2,
							borderRadius: 1.5,
							backgroundColor: "rgba(85, 105, 255, 0.02)",
						}}
					>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={4}>
								<TextField
									fullWidth
									size="small"
									type="number"
									label="Ticket Quantity"
									required
									inputProps={{ min: 1 }}
									{...register("ticket_quantity", {
										required: "Quantity is required",
										min: { value: 1, message: "Minimum quantity is 1" },
									})}
									error={Boolean(errors.ticket_quantity)}
									helperText={errors.ticket_quantity?.message}
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Controller
									name="payment_status"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											select
											fullWidth
											size="small"
											label="Payment Status"
											required
										>
											<MenuItem value="paid">Paid</MenuItem>
											<MenuItem value="unpaid">Unpaid</MenuItem>
											<MenuItem value="free">Free</MenuItem>
										</TextField>
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Controller
									name="payment_method"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											select
											fullWidth
											size="small"
											label="Payment Method"
											required
										>
											<MenuItem value="cash">Cash</MenuItem>

											<MenuItem value="online">Online Payment</MenuItem>
										</TextField>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									multiline
									rows={2}
									fullWidth
									size="small"
									label="Payment Note / Remarks"
									{...register("ticket_note")}
									placeholder="e.g. Collected at front desk"
								/>
							</Grid>
						</Grid>
					</Paper>
				</Box>
			</DialogContent>

			<Divider />

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button
					onClick={onDialogClose}
					variant="outlined"
					disabled={isPending}
					sx={{
						borderColor: "grey.400",
						color: "grey.700",
						"&:hover": { backgroundColor: "grey.100" },
					}}
				>
					Cancel
				</Button>
				<LoadingButton
					onClick={handleSubmit(onSubmit)}
					variant="contained"
					color="primary"
					loading={isPending}
				>
					<span>Create Visitor</span>
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default CreateVisitor;
